import React from 'react';
import {connect} from 'react-redux';
import compose from 'recompose/compose';
import {isFunction, omit} from "../utils/DBUtils";
import withHandlers from "recompose/withHandlers";
import {
  registerSection,
  toggleSectionOpen,
  unregisterSection
} from "../utils/sectionManagement/actions";
import lifecycle from "recompose/lifecycle";
import {getUserGroup} from 'pages/Profile/selectors';
import blockParentUpdates from "./blockParentUpdates";
import catchErrors from "./catchErrors";

/**
 * Connects a component to the Redux store and returns a new component
 * Connected component will be auto registered for validation based on store state
 *
 * New component with expect following props
 * {
 *    index: Number - Index of this section under its parent (0 is first)
 *    formProxy: ResourceFormProxy - Proxy to the form that generated the component
 *    section: Object - Config object used to generate this section
 *    reducerKey: String - Key used to access meta information in state
 *    openSection: Boolean -
 *    sectionGroup: String - Group this section is apart of
 *    sectionIndex: Number - Index of this section under its parent (0 is first)
 *    sectionKey: String - Unique key for this section
 * }
 *
 * The target component will receive the following props.
 * {
 *    active: Boolean - Is this section active
 *    hasFields: Boolean - Does this section have fields in it
 *    showSection: Boolean - Should this section be displayed
 *    openSection: Boolean -
 *    animate: Boolean - Should this section animate
 *    hasError: Boolean - Do any of this sections fields have an error
 *    header: String - Header node for section
 *    headerKey: String - Intl key for section header
 *    innerHeader: String - Other header option
 *    deletable: Boolean - Is the resource this section is displaying deletable
 *    className: String - Additional css classes for component
 *    toggleOpen: () => void - Toggle this section's active state
 * }
 *
 * The target component will also receive all values on the new components section.props object
 *
 * @param Target - Component to wrap
 * @return Node - New component
 */
const connectBreakSection = (
  Target
) => {
  const mapState = (state, ownProps) => {
    const userGroup = getUserGroup(state);

    const groupKey = ownProps.sectionGroup || ownProps.sectionKey || "global";

    // Figure current status for section
    const group = state[ownProps.reducerKey].sectionsActiveGroups[groupKey] || [];
    const accordion = group.find(section => section.key === ownProps.sectionKey) || {};
    const fields = accordion.fields || [];

    // if (ownProps.section.name === "productPanel") {
    //     debugger
    // }

    const showSection = isFunction(ownProps.section.showSection)
      ? ownProps.section.showSection(ownProps.formProxy, userGroup)
      : true;

    // explicit false if null or undefined
    let active = group ? (accordion.open || false) : false;

    let props = {
      active: active,
      hasFields: Boolean(fields.length),
      showSection,
      openSection: ownProps.sectionKey === ownProps.openSection,
      animate: ownProps.animate
    };

    // Figure out if there are any errors in this section
    if (ownProps.section.fields && ownProps.section.fields.length > 0) {
      let hasError = false;
      let errorKeys = Object.keys(state.resources.saveErrors);
      for (let key of errorKeys) {
        let error = state.resources.saveErrors[key];
        // section has an error if an save error contains this sections key in its path
        if (error.sectionKeyPath && error.sectionKeyPath.indexOf(ownProps.sectionKey) > -1) {
          hasError = true;
          break;
        }
      }
      props.hasError = hasError;
    }

    // Props are not required on sections
    if (ownProps.section.props) {

      // Since the function reference to 'getCustomSectionHeader' doesn't change,
      // just passing it directly down directly will not cause a re-render
      // So we pass the result of the 'getCustomSectionHeader' function which can change
      if (ownProps.section.props.getCustomSectionHeader) {
        props.header = ownProps.section.props.getCustomSectionHeader(ownProps.formProxy.currentResource, ownProps.sectionIndex);
      } else if (ownProps.section.props.getHeader) {
        props.header = ownProps.section.props.getHeader(ownProps.formProxy.currentResource);
      }

      // Intl key for section header
      if (ownProps.section.props.getHeaderKey) {
        props.headerKey = ownProps.section.props.getHeaderKey(ownProps.formProxy.currentResource);
      }

      // Header formatting for section
      if (ownProps.section.props.getInnerHeader) {
        props.innerHeader = ownProps.section.props.getInnerHeader;
      }

      // Show delete button (Note: only supported in ResourceAccordion component currently)
      if ( ownProps.section.deletable || ownProps.section.canDelete ) {
        props.deletable = ownProps.section.deletable && (!ownProps.section.canDelete || ownProps.section.canDelete(ownProps.formProxy));
      }

      // Additional class names to apply to section element
      if (ownProps.section.props.getClassName) {
        props.className = ownProps.section.props.getClassName(ownProps.formProxy.currentResource);
      }

      // Recreate props object passing any section props specified in config
      // to be overridden by any props calculated here
      props = {...ownProps.section.props, ...props};
    }

    return props;
  };

  const mapDispatch = (dispatch, {sectionGroup, sectionKey}) => {
    return {
      registerSection: () => dispatch(registerSection(sectionGroup, sectionKey)),
      unregisterSection: () => dispatch(unregisterSection(sectionGroup, sectionKey)),
      toggleOpen: shouldScroll => {
        dispatch(toggleSectionOpen(sectionGroup, sectionKey, shouldScroll));
      }
    };
  };

  const Hider = (props) => props.showSection ? <Target {...props}/> : null;

  return compose(
    catchErrors,
    // Components wrapped with BreakField should only be updating from Redux below
    // So we block any updates that can be caused by a parent component
    blockParentUpdates,
    connect(mapState, mapDispatch),
    withHandlers({
      toggleOpen: (props) => (shouldScroll) => {
        props.toggleOpen(shouldScroll);
      },
      registerSection: (props) => () => props.registerSection(),
      unregisterSection: (props) => () => props.unregisterSection(),
    }),
    lifecycle({
      componentDidMount() {
        this.props.registerSection();
      },
      componentWillUnmount() {
        this.props.unregisterSection();
      }
    }),
    connect(() => ({}), () => ({}), (state, dispatch, ownProps) => {
      let omissions = [
        // Called in mapState to pass resulting value instead of function reference
        "getHeader",
        "getHeaderKey",
        "getInnerHeader",
        "getClassName",
        "registerSection",
        "unregisterSection"
      ];

      return omit(ownProps, ...omissions);
    })
  )(Hider);
};

export default connectBreakSection;



// WEBPACK FOOTER //
// ./public/hoc/BreakSection.jsx